import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Автоскрытие контролов по idle-таймеру.
 *
 * isInteractingRef — реф, отражающий, удерживается ли pointer внутри контролов
 * (drag ползунка и т.п.). Нельзя скрывать контролы во время drag: смена
 * pointer-events посреди нативного drag отменяет его в ряде браузеров, и
 * ползунок откатывается к стартовому значению. Реф живёт в родителе, т.к.
 * используется и обработчиками элемента (volumechange), и здесь.
 */
export const useIdleControls = ({ isPlaying, menusOpen, idleTimeout, isInteractingRef }) => {
	const [showControls, setShowControls] = useState(true);
	const idleTimerRef = useRef(null);

	const resetIdleTimer = useCallback(() => {
		setShowControls(true);
		if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
		if (isPlaying && !menusOpen) {
			idleTimerRef.current = setTimeout(() => {
				if (!isInteractingRef.current) {
					setShowControls(false);
				}
			}, idleTimeout);
		}
	}, [isPlaying, menusOpen, idleTimeout, isInteractingRef]);

	const markInteracting = useCallback(() => {
		isInteractingRef.current = true;
		if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
		const stop = () => {
			isInteractingRef.current = false;
			window.removeEventListener('pointerup', stop);
			window.removeEventListener('pointercancel', stop);
			// после окончания drag контролы скрываются по idle-таймеру
			resetIdleTimer();
		};
		window.addEventListener('pointerup', stop);
		window.addEventListener('pointercancel', stop);
	}, [resetIdleTimer, isInteractingRef]);

	useEffect(() => {
		resetIdleTimer();
		return () => {
			if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
		};
	}, [isPlaying, menusOpen, resetIdleTimer]);

	return { showControls, setShowControls, resetIdleTimer, markInteracting };
};
